import React, { useState, useEffect, useCallback } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import { i18n } from "../../translate/i18n";
import { MenuItem, FormControl, InputLabel, Select, Typography, Divider, Tabs, Tab, Box, Chip, Tooltip } from "@material-ui/core";
import { Visibility, VisibilityOff, InfoOutlined } from "@material-ui/icons";
import { InputAdornment, IconButton, Paper } from "@material-ui/core";
import QueueSelectSingle from "../../components/QueueSelectSingle";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
    root: {
        display: "flex",
        flexWrap: "wrap",
    },
    multFieldLine: {
        display: "flex",
        "& > *:not(:last-child)": {
            marginRight: theme.spacing(1),
        },
    },
    btnWrapper: {
        position: "relative",
    },
    buttonProgress: {
        color: green[500],
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
    formControl: {
        margin: theme.spacing(1),
        minWidth: 120,
    },
    colorAdorment: {
        width: 20,
        height: 20,
    },
    sectionTitle: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(1),
        fontWeight: 600,
        color: theme.palette.primary.main,
    },
    sectionDivider: {
        marginBottom: theme.spacing(1),
    },
    helperChip: {
        margin: theme.spacing(0.5),
        fontSize: "0.7rem",
    },
    variablesBox: {
        padding: theme.spacing(1),
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)",
        borderRadius: 8,
    },
    tabPanel: {
        padding: theme.spacing(1, 0),
    },
}));

const PromptSchema = Yup.object().shape({
    name: Yup.string().min(5, i18n.t("promptModal.validation.tooShort")).max(100, i18n.t("promptModal.validation.tooLong")).required(i18n.t("promptModal.validation.required")),
    prompt: Yup.string().min(50, i18n.t("promptModal.validation.tooShort")).required(i18n.t("promptModal.validation.promptRequired")),
    voice: Yup.string().required(i18n.t("promptModal.validation.voiceRequired")),
    max_tokens: Yup.number().required(i18n.t("promptModal.validation.maxTokensRequired")),
    temperature: Yup.number().required(i18n.t("promptModal.validation.temperatureRequired")),
    apikey: Yup.string().required(i18n.t("promptModal.validation.apikeyRequired")),
    queueId: Yup.number().required(i18n.t("promptModal.validation.queueRequired")),
    max_messages: Yup.number().required(i18n.t("promptModal.validation.maxMessagesRequired"))
});

const TabPanel = ({ children, value, index }) => (
    <div role="tabpanel" hidden={value !== index} style={{ paddingTop: 8 }}>
        {value === index && children}
    </div>
);

const PromptModal = ({ open, onClose, promptId }) => {
    const classes = useStyles();
    const [selectedVoice, setSelectedVoice] = useState("texto");
    const [showApiKey, setShowApiKey] = useState(false);
    const [activeTab, setActiveTab] = useState(0);

    const handleToggleApiKey = () => {
        setShowApiKey(!showApiKey);
    };

    const initialState = {
        name: "",
        prompt: "",
        voice: "texto",
        voiceKey: "",
        voiceRegion: "",
        maxTokens: 100,
        temperature: 1,
        apiKey: "",
        queueId: null,
        maxMessages: 10,
        greetingMessage: "",
        model: "gpt-4.1-mini",
        provider: "openai",
        transferMessage: "",
        errorMessage: "",
        transferKeywords: "",
    };

    const [prompt, setPrompt] = useState(initialState);

    useEffect(() => {
        const fetchPrompt = async () => {
            if (!promptId) {
                setPrompt(initialState);
                return;
            }
            try {
                const { data } = await api.get(`/prompt/${promptId}`);
                setPrompt(prevState => {
                    return { ...prevState, ...data };
                });
                setSelectedVoice(data.voice);
            } catch (err) {
                toastError(err);
            }
        };

        fetchPrompt();
    }, [promptId, open]);

    const handleClose = () => {
        setPrompt(initialState);
        setSelectedVoice("texto");
        setActiveTab(0);
        onClose();
    };

    const handleChangeVoice = (e) => {
        setSelectedVoice(e.target.value);
    };

    const handleSavePrompt = async values => {
        const promptData = { ...values, voice: selectedVoice };
        if (!values.queueId) {
            toastError(i18n.t("promptModal.validation.missingQueue"));
            return;
        }
        try {
            if (promptId) {
                await api.put(`/prompt/${promptId}`, promptData);
            } else {
                await api.post("/prompt", promptData);
            }
            toast.success(i18n.t("promptModal.success"));
        } catch (err) {
            toastError(err);
        }
        handleClose();
    };

    return (
        <div className={classes.root}>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                scroll="paper"
                fullWidth
            >
                <DialogTitle id="form-dialog-title">
                    {promptId
                        ? `${i18n.t("promptModal.title.edit")}`
                        : `${i18n.t("promptModal.title.add")}`}
                </DialogTitle>
                <Formik
                    initialValues={prompt}
                    enableReinitialize={true}
                    onSubmit={(values, actions) => {
                        setTimeout(() => {
                            handleSavePrompt(values);
                            actions.setSubmitting(false);
                        }, 400);
                    }}
                >
                    {({ touched, errors, isSubmitting, values }) => (
                        <Form style={{ width: "100%" }}>
                            <DialogContent dividers>
                                <Tabs
                                    value={activeTab}
                                    onChange={(_, v) => setActiveTab(v)}
                                    indicatorColor="primary"
                                    textColor="primary"
                                    variant="fullWidth"
                                    style={{ marginBottom: 8 }}
                                >
                                    <Tab label={`⚙️ ${i18n.t("promptModal.tabs.general")}`} />
                                    <Tab label={`🤖 ${i18n.t("promptModal.tabs.aiModel")}`} />
                                    <Tab label={`💬 ${i18n.t("promptModal.tabs.messages")}`} />
                                    <Tab label={`🔊 ${i18n.t("promptModal.tabs.voice")}`} />
                                </Tabs>

                                {/* ═══ ABA GERAL ═══ */}
                                <TabPanel value={activeTab} index={0}>
                                    <Field
                                        as={TextField}
                                        label={i18n.t("promptModal.form.name")}
                                        name="name"
                                        error={touched.name && Boolean(errors.name)}
                                        helperText={touched.name && errors.name}
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                        required
                                    />
                                    <QueueSelectSingle />

                                    <FormControl fullWidth margin="dense" variant="outlined">
                                        <Field
                                            as={TextField}
                                            label={i18n.t("promptModal.form.apikey")}
                                            name="apiKey"
                                            type={showApiKey ? 'text' : 'password'}
                                            error={touched.apiKey && Boolean(errors.apiKey)}
                                            helperText={touched.apiKey && errors.apiKey}
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                            required
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton onClick={handleToggleApiKey}>
                                                            {showApiKey ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </FormControl>
                                </TabPanel>

                                {/* ═══ ABA IA & MODELO ═══ */}
                                <TabPanel value={activeTab} index={1}>
                                    <div className={classes.multFieldLine}>
                                        <FormControl fullWidth margin="dense" variant="outlined">
                                            <InputLabel>{i18n.t("promptModal.form.provider")}</InputLabel>
                                            <Field as={Select} name="provider" label={i18n.t("promptModal.form.provider")} labelWidth={70}>
                                                <MenuItem value="openai">OpenAI</MenuItem>
                                                <MenuItem value="gemini">Google Gemini</MenuItem>
                                            </Field>
                                        </FormControl>
                                        <FormControl fullWidth margin="dense" variant="outlined">
                                            <InputLabel>{i18n.t("promptModal.form.model")}</InputLabel>
                                            <Field as={Select} name="model" label={i18n.t("promptModal.form.model")} labelWidth={55}>
                                                {values.provider === "openai" ? [
                                                    <MenuItem key="gpt-4.1-mini" value="gpt-4.1-mini">{`GPT-4.1 Mini ${i18n.t("promptModal.form.modelRecommended")}`}</MenuItem>,
                                                    <MenuItem key="gpt-4.1" value="gpt-4.1">GPT-4.1</MenuItem>,
                                                    <MenuItem key="gpt-4.1-nano" value="gpt-4.1-nano">{`GPT-4.1 Nano ${i18n.t("promptModal.form.modelEconomic")}`}</MenuItem>,
                                                    <MenuItem key="gpt-4o" value="gpt-4o">GPT-4o</MenuItem>,
                                                    <MenuItem key="gpt-4o-mini" value="gpt-4o-mini">GPT-4o Mini</MenuItem>,
                                                    <MenuItem key="gpt-3.5-turbo" value="gpt-3.5-turbo">GPT-3.5 Turbo</MenuItem>,
                                                ] : [
                                                    <MenuItem key="gemini-2.5-flash" value="gemini-2.5-flash">{`Gemini 2.5 Flash ${i18n.t("promptModal.form.modelRecommended")}`}</MenuItem>,
                                                    <MenuItem key="gemini-2.5-pro" value="gemini-2.5-pro">Gemini 2.5 Pro</MenuItem>,
                                                    <MenuItem key="gemini-2.0-flash" value="gemini-2.0-flash">Gemini 2.0 Flash</MenuItem>,
                                                    <MenuItem key="gemini-1.5-flash" value="gemini-1.5-flash">Gemini 1.5 Flash</MenuItem>,
                                                    <MenuItem key="gemini-1.5-pro" value="gemini-1.5-pro">Gemini 1.5 Pro</MenuItem>,
                                                ]}
                                            </Field>
                                        </FormControl>
                                    </div>

                                    <Field
                                        as={TextField}
                                        label={i18n.t("promptModal.form.trainingLabel")}
                                        name="prompt"
                                        error={touched.prompt && Boolean(errors.prompt)}
                                        helperText={touched.prompt && errors.prompt}
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                        required
                                        rows={8}
                                        multiline={true}
                                        placeholder={i18n.t("promptModal.form.trainingPlaceholder")}
                                    />

                                    <Paper variant="outlined" className={classes.variablesBox}>
                                        <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
                                            {i18n.t("promptModal.variables.availableInPrompt")}
                                        </Typography>
                                        <div style={{ marginTop: 4, display: "flex", flexWrap: "wrap", gap: 4 }}>
                                            {[
                                                { v: "{{name}}", d: i18n.t("promptModal.variables.contactName") },
                                                { v: "{{firstName}}", d: i18n.t("promptModal.variables.firstName") },
                                                { v: "{{queue}}", d: i18n.t("promptModal.variables.queueName") },
                                                { v: "{{userName}}", d: i18n.t("promptModal.variables.agentName") },
                                                { v: "{{date}}", d: i18n.t("promptModal.variables.currentDate") },
                                                { v: "{{hour}}", d: i18n.t("promptModal.variables.currentHour") },
                                                { v: "{{ms}}", d: i18n.t("promptModal.variables.greeting") },
                                            ].map(item => (
                                                <Tooltip key={item.v} title={item.d} arrow>
                                                    <Chip
                                                        size="small"
                                                        label={item.v}
                                                        className={classes.helperChip}
                                                        onClick={() => navigator.clipboard.writeText(item.v)}
                                                        clickable
                                                    />
                                                </Tooltip>
                                            ))}
                                        </div>
                                    </Paper>

                                    <div className={classes.multFieldLine}>
                                        <Field
                                            as={TextField}
                                            label={i18n.t("promptModal.form.temperature")}
                                            name="temperature"
                                            type="number"
                                            error={touched.temperature && Boolean(errors.temperature)}
                                            helperText={i18n.t("promptModal.form.temperatureHelper")}
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                            inputProps={{ min: 0, max: 2, step: 0.1 }}
                                        />
                                        <Field
                                            as={TextField}
                                            label={i18n.t("promptModal.form.max_tokens")}
                                            name="maxTokens"
                                            type="number"
                                            error={touched.maxTokens && Boolean(errors.maxTokens)}
                                            helperText={i18n.t("promptModal.form.maxTokensHelper")}
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                            inputProps={{ min: 50, max: 8000 }}
                                        />
                                        <Field
                                            as={TextField}
                                            label={i18n.t("promptModal.form.max_messages")}
                                            name="maxMessages"
                                            type="number"
                                            error={touched.maxMessages && Boolean(errors.maxMessages)}
                                            helperText={i18n.t("promptModal.form.maxMessagesHelper")}
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                            inputProps={{ min: 1, max: 50 }}
                                        />
                                    </div>
                                </TabPanel>

                                {/* ═══ ABA MENSAGENS ═══ */}
                                <TabPanel value={activeTab} index={2}>
                                    <Field
                                        as={TextField}
                                        label={i18n.t("promptModal.form.greetingMessage")}
                                        name="greetingMessage"
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                        rows={3}
                                        multiline={true}
                                        placeholder={i18n.t("promptModal.form.greetingPlaceholder")}
                                        helperText={i18n.t("promptModal.form.greetingHelper")}
                                    />

                                    <Field
                                        as={TextField}
                                        label={i18n.t("promptModal.form.transferMessage")}
                                        name="transferMessage"
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                        rows={2}
                                        multiline={true}
                                        placeholder={i18n.t("promptModal.form.transferPlaceholder")}
                                        helperText={i18n.t("promptModal.form.transferHelper")}
                                    />

                                    <Field
                                        as={TextField}
                                        label={i18n.t("promptModal.form.errorMessage")}
                                        name="errorMessage"
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                        rows={2}
                                        multiline={true}
                                        placeholder={i18n.t("promptModal.form.errorPlaceholder")}
                                        helperText={i18n.t("promptModal.form.errorHelper")}
                                    />

                                    <Divider style={{ margin: "16px 0 8px" }} />
                                    <Typography className={classes.sectionTitle} variant="subtitle2">
                                        {i18n.t("promptModal.form.transferKeywordsTitle")}
                                    </Typography>

                                    <Field
                                        as={TextField}
                                        label={i18n.t("promptModal.form.transferKeywords")}
                                        name="transferKeywords"
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                        rows={3}
                                        multiline={true}
                                        placeholder={i18n.t("promptModal.form.transferKeywordsPlaceholder")}
                                        helperText={i18n.t("promptModal.form.transferKeywordsHelper")}
                                    />

                                    <Paper variant="outlined" className={classes.variablesBox}>
                                        <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
                                            {i18n.t("promptModal.variables.availableInMessages")}
                                        </Typography>
                                        <div style={{ marginTop: 4, display: "flex", flexWrap: "wrap", gap: 4 }}>
                                            {[
                                                { v: "{{name}}", d: i18n.t("promptModal.variables.contactName") },
                                                { v: "{{firstName}}", d: i18n.t("promptModal.variables.firstName") },
                                                { v: "{{ms}}", d: i18n.t("promptModal.variables.greeting") },
                                                { v: "{{userName}}", d: i18n.t("promptModal.variables.agentName") },
                                                { v: "{{queue}}", d: i18n.t("promptModal.variables.queueName") },
                                            ].map(item => (
                                                <Tooltip key={item.v} title={item.d} arrow>
                                                    <Chip
                                                        size="small"
                                                        label={item.v}
                                                        className={classes.helperChip}
                                                        onClick={() => navigator.clipboard.writeText(item.v)}
                                                        clickable
                                                    />
                                                </Tooltip>
                                            ))}
                                        </div>
                                    </Paper>
                                </TabPanel>

                                {/* ═══ ABA VOZ ═══ */}
                                <TabPanel value={activeTab} index={3}>
                                    <FormControl fullWidth margin="dense" variant="outlined">
                                        <InputLabel>{i18n.t("promptModal.form.voiceMode")}</InputLabel>
                                        <Select
                                            id="type-select"
                                            labelWidth={130}
                                            name="voice"
                                            value={selectedVoice}
                                            onChange={handleChangeVoice}
                                            multiple={false}
                                        >
                                            <MenuItem value="texto">{i18n.t("promptModal.form.voiceText")}</MenuItem>
                                            <MenuItem value="pt-BR-FranciscaNeural">Francisca</MenuItem>
                                            <MenuItem value="pt-BR-AntonioNeural">Antônio</MenuItem>
                                            <MenuItem value="pt-BR-BrendaNeural">Brenda</MenuItem>
                                            <MenuItem value="pt-BR-DonatoNeural">Donato</MenuItem>
                                            <MenuItem value="pt-BR-ElzaNeural">Elza</MenuItem>
                                            <MenuItem value="pt-BR-FabioNeural">Fábio</MenuItem>
                                            <MenuItem value="pt-BR-GiovannaNeural">Giovanna</MenuItem>
                                            <MenuItem value="pt-BR-HumbertoNeural">Humberto</MenuItem>
                                            <MenuItem value="pt-BR-JulioNeural">Julio</MenuItem>
                                            <MenuItem value="pt-BR-LeilaNeural">Leila</MenuItem>
                                            <MenuItem value="pt-BR-LeticiaNeural">Letícia</MenuItem>
                                            <MenuItem value="pt-BR-ManuelaNeural">Manuela</MenuItem>
                                            <MenuItem value="pt-BR-NicolauNeural">Nicolau</MenuItem>
                                            <MenuItem value="pt-BR-ValerioNeural">Valério</MenuItem>
                                            <MenuItem value="pt-BR-YaraNeural">Yara</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <div className={classes.multFieldLine}>
                                        <Field
                                            as={TextField}
                                            label={i18n.t("promptModal.form.voiceKeyAzure")}
                                            name="voiceKey"
                                            error={touched.voiceKey && Boolean(errors.voiceKey)}
                                            helperText={touched.voiceKey && errors.voiceKey}
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                        />
                                        <Field
                                            as={TextField}
                                            label={i18n.t("promptModal.form.voiceRegionAzure")}
                                            name="voiceRegion"
                                            error={touched.voiceRegion && Boolean(errors.voiceRegion)}
                                            helperText={touched.voiceRegion && errors.voiceRegion}
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                        />
                                    </div>
                                    <Typography variant="caption" color="textSecondary" style={{ marginTop: 8, display: "block" }}>
                                        {i18n.t("promptModal.form.voiceOnlyOpenAiWarning")}
                                    </Typography>
                                </TabPanel>
                            </DialogContent>
                            <DialogActions>
                                <Button
                                    onClick={handleClose}
                                    color="secondary"
                                    disabled={isSubmitting}
                                    variant="outlined"
                                >
                                    {i18n.t("promptModal.buttons.cancel")}
                                </Button>
                                <Button
                                    type="submit"
                                    color="primary"
                                    disabled={isSubmitting}
                                    variant="contained"
                                    className={classes.btnWrapper}
                                >
                                    {promptId
                                        ? `${i18n.t("promptModal.buttons.okEdit")}`
                                        : `${i18n.t("promptModal.buttons.okAdd")}`}
                                    {isSubmitting && (
                                        <CircularProgress
                                            size={24}
                                            className={classes.buttonProgress}
                                        />
                                    )}
                                </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>
        </div>
    );
};

export default PromptModal;